<template lang="html">
  <div>
    <!-- Images and audio -->

    <b-row class="mt-4">
      <b-col md="12" offset="0">
        <!-- Images -->
        <b-btn
          size="sm"
          variant="light"
          class="float-left"
          :disabled="$store.state.imageImportLimit - show_images.length <= 0"
          @click="selectImages"
        >
          Add additional images <v-icon name="images" />
        </b-btn>
        <span v-if="image_files.length > 0" class="float-left mt-1 ml-3">
          {{ image_files.length }}
          {{ "image" | pluralize(image_files.length) }} selected for import
        </span>
        <span v-else class="float-left mt-1 ml-3 text-muted small">
          {{ show_images.length }}
          {{ "image" | pluralize(show_images.length) }} imported.
          <span v-if="$store.getters['show/isCurrentShowTimelineEdited']">
            Open in Timeline Editor to arrange.
          </span>
        </span>
        <span
          v-if="$store.state.imageImportLimit - show_images.length <= 0"
          class="float-left mt-1 ml-1 text-muted small"
        >
          Maximum imported. Remove images before importing additional images.
        </span>
      </b-col>
    </b-row>

    <b-row v-if="$store.state.user_doc.beta_tester">
      <b-col class="mt-3">
        <b-btn
          size="sm"
          variant="light"
          class="float-left"
          :disabled="$store.state.videoImportLimit - show_videos.length <= 0"
          @click="selectVideo"
        >
          Import video slides <v-icon name="video" />
        </b-btn>
        <span v-if="validVideos.length > 0" class="float-left mt-1 ml-3">
          {{ validVideos.length }}
          {{ "video" | pluralize(validVideos.length) }} selected for import
        </span>
        <span v-else class="float-left mt-1 ml-3 text-muted small">
          <span class="mark mr-2">
            <v-icon name="flask" class="mr-1" /> Beta Feature
          </span>
          <span v-if="show_videos.length > 0">
            {{ show_videos.length }}
            {{ "video" | pluralize(show_videos.length) }} imported.
          </span>
          <span v-if="$store.state.videoImportLimit - show_videos.length <= 0">
            Maximum imported. Remove videos before importing additional videos.
          </span>
          <span v-else>
            Import video clips (mp4, mov) less than
            {{ videoDurationLimitSeconds }} seconds.
          </span>
        </span>
      </b-col>
    </b-row>

    <b-row v-if="$store.state.user_doc.beta_tester && invalidVideos.length > 0">
      <b-col md="6" class="mt-3 alert alert-warning">
        <p v-if="invalidVideos.length > 0" class="mt-1">
          Warning: {{ invalidVideos.length }} selected
          {{ "video" | pluralize(invalidVideos.length) }} exceeded
          {{ videoDurationLimitSeconds }} seconds.
        </p>
        <p v-for="i in invalidVideos" :key="i.name" class="small">
          <strong>{{ i.name }}</strong> will not be imported.
        </p>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col md="12" offset="0">
        <!-- Audio -->
        <b-btn
          size="sm"
          variant="light"
          class="float-left"
          @click="selectAudio"
        >
          Import new audio <v-icon name="headphones" />
        </b-btn>
        <b-btn
          v-if="!displayMusicLibrary"
          size="sm"
          variant="light"
          class="float-left ml-1"
          @click="displayMusicLibrary = !displayMusicLibrary"
        >
          Select new from library <v-icon name="music" />
        </b-btn>
        <b-btn
          v-if="displayMusicLibrary"
          size="sm"
          variant="outline-primary"
          class="float-left ml-1"
          @click="displayMusicLibrary = !displayMusicLibrary"
        >
          Hide library <v-icon name="music" />
        </b-btn>
        <span v-if="!audio_file" class="float-left mt-1 ml-3 text-muted small">
          Current audio: {{ show_audio.name }} ({{
            (show_audio.duration * 1000) | toMMSS
          }})
        </span>
        <span v-if="audio_file" class="float-left mt-1 ml-3">
          New audio selected for upload: <strong>{{ audio_file.name }}</strong>
        </span>
      </b-col>
    </b-row>

    <b-row v-if="displayMusicLibrary">
      <b-col md="10" offset-md="1" class="mt-5">
        <h4 class="mb-5 text-center">
          Select a new music track from our pre-licensed music library
          <v-icon name="music" />
        </h4>
        <MusicLibrarySelector
          @close="displayMusicLibrary = !displayMusicLibrary"
        />
      </b-col>
    </b-row>

    <!-- Asset importer -->
    <b-row v-if="show_bound && show_asset_importer">
      <b-col v-if="uploadIsValid" md="10" offset="1" class="mt-3">
        <AssetImporter
          :show="show"
          :image_files="image_files"
          :audio_file="audio_file"
          :video_files="validVideos"
          @uploadStarted="uploadStarted"
        />
      </b-col>
      <b-col
        v-if="
          image_files.length + show_images.length >
            $store.state.imageImportLimit && image_files.length != 0
        "
        md="10"
        offset="1"
        class="mt-5 alert alert-warning"
      >
        <div>
          <div
            v-if="$store.state.imageImportLimit == maxImagesPerShow"
            class="mt-1"
          >
            <p>
              The maximum is
              {{ $store.state.imageImportLimit }}
              images per show.
            </p>
          </div>
          <div v-else class="mt-1">
            <p>
              Your <strong>{{ $store.getters.user_plan.name }}</strong> account
              enables
              {{ $store.state.imageImportLimit }}
              images per show.

              <b-link :to="{ name: 'upgrade_view' }" target="_blank">
                View account options <v-icon name="external-link-alt" />
              </b-link>
            </p>
          </div>
          <p>
            You have {{ show_images.length }} images imported. You can import an
            additional
            <strong>
              {{ $store.state.imageImportLimit - show_images.length }}
              {{
                "image"
                  | pluralize(
                    $store.state.imageImportLimit - show_images.length
                  )
              }}
            </strong>
            into this show.
          </p>
          <p class="mb-1">
            Please
            <b-link @click="selectImages">
              re-select your images <v-icon name="images" />
            </b-link>
            for import, or remove images to make space for additional images.
          </p>
        </div>
      </b-col>
      <b-col
        v-if="
          validVideos.length + show_videos.length >
            $store.state.videoImportLimit && validVideos.length != 0
        "
        md="10"
        offset="1"
        class="mt-5 alert alert-warning"
      >
        <div>
          <div class="mt-1">
            <p>
              Your <strong>{{ $store.getters.user_plan.name }}</strong> account
              enables
              {{ $store.state.videoImportLimit }}
              videos per show.

              <b-link :to="{ name: 'upgrade_view' }" target="_blank">
                View account options <v-icon name="external-link-alt" />
              </b-link>
            </p>
          </div>
          <p>
            You have {{ show_videos.length }} videos imported. You can import an
            additional
            <strong>
              {{ $store.state.videoImportLimit - show_videos.length }}
              {{
                "video"
                  | pluralize(
                    $store.state.videoImportLimit - show_videos.length
                  )
              }}
            </strong>
            into this show.
          </p>
          <p class="mb-1">
            Please
            <b-link @click="selectVideo">
              re-select your videos <v-icon name="video" />
            </b-link>
            for import, or remove videos to make space for additional videos.
          </p>
        </div>
      </b-col>
    </b-row>
    <input
      v-show="false"
      ref="audioSelect"
      type="file"
      :accept="$store.state.acceptedAudioFormats"
      class="input-file"
      @change="audioFileChange($event.target.name, $event.target.files)"
    />
    <input
      v-show="false"
      ref="imageSelect"
      type="file"
      multiple
      :accept="$store.state.acceptedImageFormats"
      class="input-file"
      @change="imageFilesChange($event.target.name, $event.target.files)"
    />
    <input
      v-show="false"
      ref="videoSelect"
      type="file"
      :multiple="true"
      :accept="$store.state.acceptedVideoFormats"
      class="input-file"
      @change="videoFilesChange($event.target.name, $event.target.files)"
    />
  </div>
</template>

<script>
import AssetImporter from "@/components/show/AssetImporter";
import MusicLibrarySelector from "@/components/show/MusicLibrarySelector";
import map from "lodash/map";
import each from "lodash/each";
import filter from "lodash/filter";
import { maxImagesPerShow } from "@/defaults";
export default {
  name: "ImporterAdditional",
  components: {
    AssetImporter,
    MusicLibrarySelector
  },
  filters: {
    pluralize(word, amount) {
      if (amount > 1) {
        return `${word}s`;
      }
      return word;
    },
    toMMSS: function(s) {
      // Pad to 2 or 3 digits, default is 2
      function pad(n, z) {
        z = z || 2;
        return ("00" + n).slice(-z);
      }

      var ms = s % 1000;
      s = (s - ms) / 1000;
      var secs = s % 60;
      s = (s - secs) / 60;
      var mins = s % 60;

      return pad(mins) + ":" + pad(secs);
    }
  },
  data() {
    return {
      image_files: [],
      audio_file: null,
      show_bound: true, // Always true at this point,
      uploadingHasBegun: false,
      displayMusicLibrary: false,
      video_files: [],
      videoDurationLimitSeconds:
        this.$store.state.user_doc.video_slides_duration_limit ||
        this.$store.state.videoImportLimitSeconds,
      validVideos: [],
      invalidVideos: [],
      maxImagesPerShow: maxImagesPerShow
    };
  },
  computed: {
    show() {
      return this.$store.state.show.currentShow;
    },
    show_audio() {
      if (this.show.data) {
        return this.show.data.audio;
      }
      return null;
    },
    show_images() {
      if (this.show.data.images) {
        return filter(this.show.data.images, i => {
          return i.type != "video";
        });
      }
      return [];
    },
    show_videos() {
      if (this.show.data.images) {
        return filter(this.show.data.images, i => {
          return i.type == "video";
        });
      }
      return [];
    },
    uploadIsValid() {
      if (this.image_files.length == 0 && this.validVideos.length == 0) {
        // Only audio is being imported. Let it pass.
        return true;
      }
      return (
        this.image_files.length + this.show_images.length <=
          this.$store.state.imageImportLimit &&
        this.validVideos.length + this.show_videos.length <=
          this.$store.state.videoImportLimit
      );
    },
    show_asset_importer() {
      // Asset importing is allowed if there are images and audio,
      // OR the show is already created
      // OR uploadingHasBegun
      if (this.uploadingHasBegun) {
        return true;
      }
      if (this.hasSlideArray) {
        // Show is already created.
        return true;
      }
      if (this.show_audio) {
        return true;
      }
      if (this.image_files.length > 0 && this.audio_file) {
        // Image and audio BOTH ready to import.
        return true;
      }
      return false;
    }
  },
  methods: {
    uploadStarted() {
      // Reset the files for the next addition.
      this.uploadingHasBegun = true;
      this.image_files = [];
      this.video_files = [];
      this.validVideos = [];
      this.invalidVideos = [];
      this.audio_file = null;
    },
    selectImages() {
      this.$refs.imageSelect.click();
    },
    selectVideo() {
      this.$refs.videoSelect.click();
    },
    videoFilesChange(fieldName, fileList) {
      this.validVideos = [];
      this.invalidVideos = [];
      if (!fileList.length) return;
      // append the files to FormData
      this.video_files = map(fileList, i => {
        return i;
      });
      this.addVideoDurationInfo();
    },
    addVideoDurationInfo() {
      var _v = this;
      each(this.video_files, video_file => {
        var video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = function() {
          window.URL.revokeObjectURL(video.src);
          var duration = video.duration;
          video_file.duration = duration;
          console.log("duration", duration);
          console.log(video_file);
          if (duration < _v.videoDurationLimitSeconds) {
            console.log("valid video");
            _v.validVideos.push(video_file);
          } else {
            console.log("invalid video");
            _v.invalidVideos.push(video_file);
          }
        };
        video.src = URL.createObjectURL(video_file);
      });
    },
    imageFilesChange(fieldName, fileList) {
      if (!fileList.length) return;
      // append the files to FormData
      this.image_files = map(fileList, i => {
        return i;
      });
    },
    selectAudio() {
      this.$refs.audioSelect.click();
    },

    audioFileChange(fieldName, fileList) {
      if (!fileList.length) return;
      // append the files to FormData
      this.audio_file = fileList[0];
    }
  }
};
</script>

<style lang="css" scoped></style>
